import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, CheckIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Lead } from '../../types/Lead';
import { University, Campus, Course, Session, Country } from '../../types/UniversityPortal';

interface AddLeadsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface LeadAssociation {
  leadId: number;
  leadName: string;
  sessionId?: number | null;
  sessionName?: string;
  universityId: number;
  universityName: string;
  campusId?: number | null;
  campusName?: string;
  courseId?: number | null;
  courseName?: string;
  countryId?: number | null;
  countryName?: string;
}

const AddLeadsModal: React.FC<AddLeadsModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Selection states
  const [selectedLeads, setSelectedLeads] = useState<Set<number>>(new Set());
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [selectedSession, setSelectedSession] = useState<number | null>(null);
  const [selectedUniversity, setSelectedUniversity] = useState<number | null>(null);
  const [selectedCampus, setSelectedCampus] = useState<number | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
  
  // Associations to be created
  const [associations, setAssociations] = useState<LeadAssociation[]>([]);
  
  // Search states
  const [leadSearch, setLeadSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [sessionSearch, setSessionSearch] = useState('');
  const [universitySearch, setUniversitySearch] = useState('');
  const [campusSearch, setCampusSearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [leadsRes, countriesRes, sessionsRes, universitiesRes] = await Promise.all([
        api.get('/leads'),
        api.get('/university-portal/countries'),
        api.get('/university-portal/sessions'),
        api.get('/university-portal/universities')
      ]);
      
      setLeads(leadsRes.data.data || []);
      setCountries(countriesRes.data.data || []);
      setSessions(sessionsRes.data.data || []);
      setUniversities(universitiesRes.data.data || []);
    } catch (err) {
      setError('Failed to fetch initial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampuses = async (universityId: number) => {
    try {
      const response = await api.get(`/university-portal/universities/${universityId}/campuses`);
      setCampuses(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch campuses:', err);
    }
  };

  const fetchCourses = async (universityId: number) => {
    try {
      const response = await api.get(`/university-portal/universities/${universityId}/courses`);
      setCourses(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
    }
  };

  const handleLeadSelect = (leadId: number) => {
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  const handleSelectAllLeads = () => {
    if (selectedLeads.size === filteredLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(filteredLeads.map(lead => lead.id)));
    }
  };

  const handleSessionSelect = (sessionId: number) => {
    setSelectedSession(sessionId);
  };

  const handleUniversitySelect = (universityId: number) => {
    setSelectedUniversity(universityId);
    setSelectedCampus(null);
    setSelectedCourse(null);
    setCampuses([]);
    setCourses([]);
    fetchCampuses(universityId);
    fetchCourses(universityId);
  };

  const handleCampusSelect = (campusId: number) => {
    setSelectedCampus(campusId);
    setSelectedCourse(null);
  };

  const handleCourseSelect = (courseId: number) => {
    setSelectedCourse(courseId);
  };

  const addAssociation = () => {
    if (selectedLeads.size === 0 || !selectedUniversity) return;

    const university = universities.find(u => u.id === selectedUniversity);
    if (!university) return;

    const session = selectedSession ? sessions.find(s => s.id === selectedSession) : undefined;

    const newAssociations: LeadAssociation[] = Array.from(selectedLeads).map(leadId => {
      const lead = leads.find(l => l.id === leadId);
      const campus = selectedCampus ? campuses.find(c => c.id === selectedCampus) : undefined;
      const course = selectedCourse ? courses.find(c => c.id === selectedCourse) : undefined;
      const country = selectedCountry ? countries.find(c => c.id === selectedCountry) : undefined;

      return {
        leadId,
        leadName: lead?.fullName || 'Unknown Lead',
        sessionId: selectedSession || null,
        sessionName: session?.name,
        universityId: selectedUniversity,
        universityName: university.name,
        campusId: selectedCampus || null,
        campusName: campus?.name,
        courseId: selectedCourse || null,
        courseName: course?.name,
        countryId: selectedCountry || null,
        countryName: country?.name
      };
    });

    setAssociations(prev => [...prev, ...newAssociations]);
    setSelectedLeads(new Set());
    setSelectedCampus(null);
    setSelectedCourse(null);
  };

  const removeAssociation = (index: number) => {
    setAssociations(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (associations.length === 0) return;

    setLoading(true);
    try {
      await api.post('/lead-associations', {
        associations: associations.map(assoc => ({
          leadId: assoc.leadId,
          sessionId: assoc.sessionId || null,
          universityId: assoc.universityId,
          campusId: assoc.campusId || null,
          courseId: assoc.courseId || null,
          countryId: assoc.countryId || null
        }))
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create associations');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setSelectedLeads(new Set());
    setSelectedCountry(null);
    setSelectedSession(null);
    setSelectedUniversity(null);
    setSelectedCampus(null);
    setSelectedCourse(null);
    setAssociations([]);
    setLeadSearch('');
    setCountrySearch('');
    setSessionSearch('');
    setUniversitySearch('');
    setCampusSearch('');
    setCourseSearch('');
    setError(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  // Filter functions
  const filteredLeads = leads.filter(lead => 
    lead.fullName.toLowerCase().includes(leadSearch.toLowerCase()) ||
    lead.email.toLowerCase().includes(leadSearch.toLowerCase())
  );

  const filteredSessions = sessions.filter(session =>
    session.name.toLowerCase().includes(sessionSearch.toLowerCase()) ||
    session.code.toLowerCase().includes(sessionSearch.toLowerCase())
  );

  const filteredUniversities = universities.filter(uni =>
    uni.name.toLowerCase().includes(universitySearch.toLowerCase())
  );

  const filteredCampuses = campuses.filter(campus =>
    campus.name.toLowerCase().includes(campusSearch.toLowerCase())
  );

  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(courseSearch.toLowerCase())
  );

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    (country.code && country.code.toLowerCase().includes(countrySearch.toLowerCase()))
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add Leads to University Portal</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                1
              </div>
              <span className="ml-2 text-sm font-medium">Select Leads</span>
            </div>
            <div className={`flex-1 h-0.5 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                2
              </div>
              <span className="ml-2 text-sm font-medium">Select Country</span>
            </div>
            <div className={`flex-1 h-0.5 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                3
              </div>
              <span className="ml-2 text-sm font-medium">Select Session</span>
            </div>
            <div className={`flex-1 h-0.5 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                4
              </div>
              <span className="ml-2 text-sm font-medium">Select University</span>
            </div>
            <div className={`flex-1 h-0.5 ${step >= 5 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            <div className={`flex items-center ${step >= 5 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 5 ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                5
              </div>
              <span className="ml-2 text-sm font-medium">Select Campus & Course</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Step 1: Select Leads */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Leads</h3>
              
              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={leadSearch}
                  onChange={(e) => setLeadSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Select All */}
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedLeads.size === filteredLeads.length && filteredLeads.length > 0}
                    onChange={handleSelectAllLeads}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Select All ({filteredLeads.length} leads)
                  </span>
                </label>
              </div>

              {/* Leads List */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredLeads.map(lead => (
                  <label key={lead.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={selectedLeads.has(lead.id)}
                      onChange={() => handleLeadSelect(lead.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-gray-900">{lead.fullName}</p>
                      <p className="text-sm text-gray-500">{lead.email}</p>
                    </div>
                  </label>
                ))}
              </div>

              {filteredLeads.length === 0 && (
                <p className="text-gray-500 text-center py-8">No leads found</p>
              )}
            </div>
          )}

          {/* Step 2: Select Country (Optional) */}
          {step === 2 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Country (Optional)</h3>
              <p className="text-sm text-gray-600 mb-4">
                You can optionally associate a country with all selected leads, or skip this step.
              </p>
              
              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Countries List */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <button
                  onClick={() => setSelectedCountry(null)}
                  className={`w-full text-left p-3 border rounded-lg hover:bg-gray-50 ${
                    selectedCountry === null
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center">
                    <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">No Country</p>
                      <p className="text-sm text-gray-500">Skip country selection</p>
                    </div>
                  </div>
                </button>
                {filteredCountries.map(country => (
                  <button
                    key={country.id}
                    onClick={() => setSelectedCountry(country.id)}
                    className={`w-full text-left p-3 border rounded-lg hover:bg-gray-50 ${
                      selectedCountry === country.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center">
                      <GlobeAltIcon className="h-5 w-5 text-teal-600 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{country.name}</p>
                        {country.code && (
                          <p className="text-sm text-gray-500">Code: {country.code}</p>
                        )}
                      </div>
                      {selectedCountry === country.id && (
                        <CheckIcon className="h-5 w-5 text-blue-600 ml-auto" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {filteredCountries.length === 0 && (
                <p className="text-gray-500 text-center py-8">No countries found</p>
              )}
            </div>
          )}

          {/* Step 3: Select Session */}
          {step === 3 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Session (Optional)</h3>
              
              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search sessions..."
                  value={sessionSearch}
                  onChange={(e) => setSessionSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Sessions List */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <button
                  onClick={() => setSelectedSession(null)}
                  className={`w-full text-left p-3 border rounded-lg hover:bg-gray-50 ${
                    selectedSession === null
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900">No Session</p>
                  <p className="text-sm text-gray-500">Skip session selection</p>
                </button>
                {filteredSessions.map(session => (
                  <button
                    key={session.id}
                    onClick={() => handleSessionSelect(session.id)}
                    className={`w-full text-left p-3 border rounded-lg hover:bg-gray-50 ${
                      selectedSession === session.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900">{session.name}</p>
                    <p className="text-sm text-gray-500">
                      {session.code} • {session.startDate ? new Date(session.startDate).toLocaleDateString() : 'No start date'} - {session.endDate ? new Date(session.endDate).toLocaleDateString() : 'No end date'}
                    </p>
                  </button>
                ))}
              </div>

              {filteredSessions.length === 0 && (
                <p className="text-gray-500 text-center py-8">No sessions found</p>
              )}
            </div>
          )}

          {/* Step 4: Select University */}
          {step === 4 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select University</h3>
              
              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search universities..."
                  value={universitySearch}
                  onChange={(e) => setUniversitySearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Universities List */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredUniversities.map(university => (
                  <button
                    key={university.id}
                    onClick={() => handleUniversitySelect(university.id)}
                    className={`w-full text-left p-3 border rounded-lg hover:bg-gray-50 ${
                      selectedUniversity === university.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900">{university.name}</p>
                    <p className="text-sm text-gray-500">{university.description || 'No description'}</p>
                  </button>
                ))}
              </div>

              {filteredUniversities.length === 0 && (
                <p className="text-gray-500 text-center py-8">No universities found</p>
              )}
            </div>
          )}

          {/* Step 5: Select Campus & Course */}
          {step === 5 && selectedUniversity && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Campus & Course (Optional)</h3>
              
              {/* Campus Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Campus (Optional)</label>
                <div className="mb-2">
                  <input
                    type="text"
                    placeholder="Search campuses..."
                    value={campusSearch}
                    onChange={(e) => setCampusSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  <button
                    onClick={() => setSelectedCampus(null)}
                    className={`w-full text-left p-3 border rounded-lg hover:bg-gray-50 ${
                      selectedCampus === null
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900">No Campus</p>
                  </button>
                  {filteredCampuses.map(campus => (
                    <button
                      key={campus.id}
                      onClick={() => handleCampusSelect(campus.id)}
                      className={`w-full text-left p-3 border rounded-lg hover:bg-gray-50 ${
                        selectedCampus === campus.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-900">{campus.name}</p>
                      <p className="text-sm text-gray-500">{campus.city}, {campus.country}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Course Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Course (Optional)</label>
                <div className="mb-2">
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  <button
                    onClick={() => setSelectedCourse(null)}
                    className={`w-full text-left p-3 border rounded-lg hover:bg-gray-50 ${
                      selectedCourse === null
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900">No Course</p>
                  </button>
                  {filteredCourses.map(course => (
                    <button
                      key={course.id}
                      onClick={() => handleCourseSelect(course.id)}
                      className={`w-full text-left p-3 border rounded-lg hover:bg-gray-50 ${
                        selectedCourse === course.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-900">{course.name}</p>
                      <p className="text-sm text-gray-500">{course.code}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Add Association Button */}
              <button
                onClick={addAssociation}
                disabled={selectedLeads.size === 0}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Association
              </button>
            </div>
          )}

          {/* Associations Summary */}
          {associations.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Associations to be Created</h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {associations.map((assoc, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{assoc.leadName}</p>
                      <p className="text-sm text-gray-500">
                        {assoc.countryName && (
                          <>
                            <GlobeAltIcon className="h-4 w-4 inline-block mr-1 text-teal-600" />
                            {assoc.countryName} →
                          </>
                        )}
                        {assoc.sessionName && `${assoc.sessionName} → `}
                        {assoc.universityName}
                        {assoc.campusName && ` → ${assoc.campusName}`}
                        {assoc.courseName && ` → ${assoc.courseName}`}
                      </p>
                    </div>
                    <button
                      onClick={() => removeAssociation(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="flex space-x-3">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Previous
              </button>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            
            {step < 5 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={
                  (step === 1 && selectedLeads.size === 0) ||
                  (step === 4 && !selectedUniversity)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={associations.length === 0 || loading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Associations'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLeadsModal;
